import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  PerformanceAnswersQuery,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceReviewEvaluationQuery,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import FullAnswerSidepanel from 'modules/performance_process/components/insights/Answers/AllAnswersTab/FullAnswerSidepanel'
import AnswersTable from 'modules/performance_process/components/insights/Answers/common/AnswersTable'
import { EmptyState } from 'modules/performance_process/components/insights/Answers/common/EmptyState'
import {
  Answer,
  extractQuestions,
  present,
  publishedAnswersWithTargetByReviewType,
} from 'modules/performance_process/components/insights/common/presenters'

type PerformanceQuestionSingleLabel = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        PerformanceReviewEvaluationQuery['performance']['reviewEvaluation']
      >['answeredQuestionnaires']
    >['answeredQuestionnaireWithSections']
  >['content'][number]['questions'][number]['question']
>

export const AllAnswersTab: React.FC<{
  answersQuery: PerformanceAnswersQuery
  reviewQuestionnaireByReviewerStrategy: PerformanceQuestionnaireByReviewerStrategyFragment
  reviewType: PerformanceReviewerStrategiesStrategyEnum
}> = ({ answersQuery, reviewQuestionnaireByReviewerStrategy, reviewType }) => {
  const [selectedQuestion, setSelectedQuestion] =
    React.useState<PerformanceQuestionSingleLabel | null>(null)
  const [selectedAnswer, setSelectedAnswer] = React.useState<Answer | null>(null)

  // Get an object for each target with its answers for the selected reviewType
  const publishedAnswersWithTarget = publishedAnswersWithTargetByReviewType(
    answersQuery.performance.reviewProcess?.materializeTargets ?? [],
    reviewType
  )

  const { targets, answers, targetsWithReviewers } = present(
    publishedAnswersWithTarget, // Array of target objects with answers, already filtered by reviewType
    reviewType
  )

  // Get questions for the selected reviewType
  const qualitativeQuestionsForStrategy = extractQuestions(
    reviewQuestionnaireByReviewerStrategy,
    reviewType
  )

  if (answers.length === 0) {
    return (
      <Box flexGrow justifyContent="center">
        <EmptyState
          title={i18n.t('performance_process.insights.answers.empty_state_all_answers')}
        />
      </Box>
    )
  }

  return (
    <>
      <AnswersTable
        targets={targets}
        questions={qualitativeQuestionsForStrategy || []}
        answers={answers}
        targetsWithReviewers={targetsWithReviewers}
        collapsable={reviewType === 'peers' || reviewType === 'direct_reports'}
        renderCell={(question, answer) => (
          <AnswersTable.AnswerCell
            answer={answer}
            onClick={() => {
              if (!answer) return
              setSelectedQuestion(question)
              setSelectedAnswer(answer)
            }}
          />
        )}
      />
      {selectedAnswer && selectedQuestion && (
        <FullAnswerSidepanel
          question={selectedQuestion}
          answer={selectedAnswer}
          onClose={() => setSelectedAnswer(null)}
        />
      )}
    </>
  )
}
